// sendGift.js
'use strict';
import fetch from '../resource/customFetch.js';
import {
  ADD_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  REMOVE_MESSAGES,
} from '../ActionTypes.js';
import { NEED_AUTH_HANDLER, JWT_SCOPE_CREATOR } from '../RemoteConfigKeys.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import trackEvent, { MESSAGE_SENT_FAILED } from '../resource/trackEvent.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import createMessage from '../resource/createMessage.js';
import addvalue from '../action/addvalue.js';
import login from '../action/login.js';
import pushToastr from '../action/pushToastr.js';
import renewPendingChatMessage from '../action/renewPendingChatMessage.js';
import addMessage from '../action/addMessage.js';
import deductDiamond from '../action/deductDiamond.js';
import getIsPermissionMatched from '../selector/getIsPermissionMatched.js';
import getMeData from '../selector/getMeData.js';
import getGiftData from '../selector/getGiftData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { Entrypoint } from '../resource/shopConstants.js';

/**
 * Send gift
 * @kind action
 * @param {string} { userId } - user id.
 * @param {string} { giftId } - gift id.
 * @param {string} { chatroomId } - chatroom id.
 * @param {string} { successTextKey } - success toastr text.
 * @return {Promise} Action promise.
 */
const sendGift =
  ({ userId, giftId, chatroomId, successTextKey }) =>
  async (dispatch, getState) => {
    const token = getMeData(getState(), 'token');
    const isCP = getIsPermissionMatched(getState(), JWT_SCOPE_CREATOR);

    if (!token) {
      const needAuthHandler = getRemoteConfigData(
        getState(),
        NEED_AUTH_HANDLER
      );
      if (needAuthHandler === 'shop') {
        return dispatch(addvalue({ entrypoint: Entrypoint.GIFT, via: 'gift' }));
      } else {
        return dispatch(login({ shouldChooseEntryOption: false }));
      }
    }

    if (isCP) {
      return dispatch(
        pushToastr({
          textKey: 'gift_permission_alert_message_3',
          durationSec: 3,
          color: 'error',
        })
      );
    }

    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    const meId = getMeData(getState(), 'id');
    const message = createMessage({
      type: 'gift',
      giftId,
      senderId: meId,
    });

    const selectPath = ['chatroom', chatroomId, 'sendChatMessage', message.id];
    const pendingListPath = ['chatroom', chatroomId, 'pendingMessages'];
    const url = getResourceUrl({
      endpoint: `/users/${userId}/gift/${giftId}`,
    });

    const isSending = getNetworkingData(getState(), selectPath, 'isFetching');

    if (isSending) {
      return;
    }

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    dispatch(
      addMessage({
        message,
      })
    );
    dispatch({
      type: ADD_LIST_ITEMS,
      payload: { selectPath: pendingListPath, itemIds: [message.id] },
    });

    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const payload = (await response.json()) || {};
      const messageId = payload.id;

      dispatch(
        addMessage({
          message: {
            ...message,
            id: messageId,
            isPending: false,
          },
        })
      );
      dispatch(
        renewPendingChatMessage({
          sentMessageId: messageId,
          message,
          chatroomId,
        })
      );
      dispatch({ type: REMOVE_MESSAGES, payload: { itemIds: [message.id] } });

      const giftPrice = getGiftData(getState(), giftId, 'price');

      dispatch(deductDiamond({ price: giftPrice }));

      if (successTextKey) {
        dispatch(
          pushToastr({
            textKey: successTextKey,
            durationSec: 3,
            color: 'success',
          })
        );
      }

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      trackEvent({
        type: MESSAGE_SENT_FAILED,
        payload: {
          reason: error.message,
          type: 'gift',
          id: null,
          mediaType: null,
        },
      });

      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default sendGift;
